import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { t } from "@/i18n";
import {
  Camera, RefreshCw, ArrowLeft, Loader2, ExternalLink,
  AlertTriangle, ChevronDown, ChevronRight, CircleCheck,
} from "lucide-react";

const CHANGE_STYLE = {
  NEW_SERVICE:      { cls: "bg-pos-green/10 text-pos-green border-pos-green/40", label: "Nieuw aanbod" },
  REMOVED_SERVICE:  { cls: "bg-warn-amber/10 text-warn-amber border-warn-amber/40",      label: "Weggevallen" },
  HERO_UPDATED:     { cls: "bg-info-blue/10 text-info-blue border-info-blue/40",          label: "Hero-copy" },
  CTA_CHANGED:      { cls: "bg-purple-50 text-purple-800 border-purple-200",    label: "CTA gewijzigd" },
  NEW_CONTENT:      { cls: "bg-bg-panel-raised text-text-primary/85 border-border-hairline",label: "Content bijgewerkt" },
};
const CONF_STYLE = {
  HIGH:   "bg-pos-green/20 text-pos-green",
  MEDIUM: "bg-warn-amber/20 text-warn-amber",
  LOW:    "bg-bg-panel-raised text-text-muted",
};

export default function CompetitorSnapshotsPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [snaps, setSnaps] = useState(null);
  const [changes, setChanges] = useState([]);
  const [competitor, setCompetitor] = useState(null);
  const [runningSnap, setRunningSnap] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [openSnap, setOpenSnap] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const [snapsResp, changesResp, compsResp] = await Promise.all([
        api.get(`/competitors/${id}/snapshots?limit=30`),
        api.get(`/competitors/changes?limit=50`),
        api.get(`/competitors`),
      ]);
      setSnaps(snapsResp.data.items || []);
      setChanges((changesResp.data.items || []).filter((x) => x.competitor_id === id));
      const c = (compsResp.data.items || compsResp.data || []).find((x) => x.id === id);
      setCompetitor(c || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || t("common.error_generic"));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const runSnapshot = async () => {
    setRunningSnap(true); setMsg(null); setErr(null);
    try {
      const { data } = await api.post(`/competitors/${id}/snapshot`);
      setMsg(
        data.changes?.length
          ? t("competitors.change_ok").replace("{n}", String(data.changes.length))
          : t("competitors.change_none")
      );
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || t("common.error_generic"));
    } finally {
      setRunningSnap(false);
    }
  };

  if (snaps === null) {
    return <AppLayout><div className="py-24 text-center text-text-muted">
      <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> {t("common.loading")}
    </div></AppLayout>;
  }

  return (
    <AppLayout>
      <button
        onClick={() => nav("/business-brain")}
        data-testid="snapshots-back"
        className="mb-4 text-xs text-accent-teal hover:text-info-blue flex items-center gap-1"
      >
        <ArrowLeft className="w-3 h-3" /> {t("competitors.snapshots_back")}
      </button>

      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Camera className="w-6 h-6 text-accent-teal" />
          <div>
            <h1 className="text-2xl font-display font-bold text-text-primary tracking-tight" data-testid="snapshots-title">
              {competitor?.display_name ? t("competitors.snapshots_title").replace("{name}", competitor.display_name) : "Concurrent — snapshots"}
            </h1>
            <p className="text-xs text-text-muted mt-0.5">
              {competitor?.domain
                ? <>Homepagina: <span className="font-mono">{competitor.domain}</span></>
                : t("competitors.snapshots_no_domain")}
            </p>
          </div>
        </div>
        <button
          disabled={runningSnap || !competitor?.domain}
          onClick={runSnapshot}
          data-testid="snapshots-run-now"
          className="h-10 px-4 rounded-md bg-accent-teal hover:bg-accent-teal-dim disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${runningSnap ? "animate-spin" : ""}`} />
          {runningSnap ? t("competitors.snapshots_running") : t("competitors.snapshots_run")}
        </button>
      </div>

      {msg && <div data-testid="snapshots-msg" className="mb-4 p-3 rounded-md border border-pos-green/40 bg-pos-green/10 text-pos-green text-sm">{msg}</div>}
      {err && <div data-testid="snapshots-err" className="mb-4 p-3 rounded-md border border-neg-red/40 bg-neg-red/10 text-neg-red text-sm flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{err}</span>
      </div>}

      {(() => {
        const hasWebsite = !!competitor?.domain;
        const nSnaps = snaps.length;
        const nChanges = changes.length;
        if (!hasWebsite) {
          return (
            <div className="max-w-[520px] mx-auto">
              <div data-testid="snapshots-empty-no-website"
                    className="rounded-xl border border-dashed border-border-hairline bg-bg-panel p-8 text-center">
                <p className="text-sm text-text-primary mb-4">{t("competitors.snapshots_empty_no_website")}</p>
                <button
                  onClick={() => nav(`/business-brain#competitor-${id}`)}
                  data-testid="snapshots-edit-competitor"
                  className="h-10 px-4 rounded-md bg-accent-teal hover:bg-accent-teal-dim text-white text-sm font-semibold"
                >
                  {t("competitors.snapshots_edit_competitor")}
                </button>
              </div>
            </div>
          );
        }
        if (nSnaps === 0) {
          return (
            <div className="max-w-[520px] mx-auto">
              <div data-testid="snapshots-empty-no-snap"
                    className="rounded-xl border border-dashed border-border-hairline bg-bg-panel p-8 text-center">
                <p className="text-sm text-text-primary mb-4">{t("competitors.snapshots_empty_no_snap")}</p>
              </div>
            </div>
          );
        }
        if (nSnaps === 1 && nChanges === 0) {
          return (
            <div className="max-w-[520px] mx-auto">
              <div data-testid="snapshots-empty-one-snap"
                    className="rounded-xl border border-dashed border-border-hairline bg-bg-panel p-8 text-center">
                <p className="text-sm text-text-primary">{t("competitors.snapshots_empty_one_snap")}</p>
              </div>
            </div>
          );
        }
        return (
      <div className="grid lg:grid-cols-5 gap-6">
        {/* Change timeline */}
        <div className="lg:col-span-3">
          <h2 className="text-xs text-text-muted font-semibold mb-3">
            Tijdlijn van veranderingen
          </h2>
          {changes.length === 0 ? (
            <div data-testid="snapshots-changes-empty" className="rounded-xl border border-dashed border-border-hairline bg-bg-panel p-8 text-center">
              <p className="text-sm text-text-muted">
                {t("competitors.snapshots_empty_one_snap")}
              </p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="snapshots-changes-list">
              {changes.map((ch) => {
                const style = CHANGE_STYLE[ch.change_type] || CHANGE_STYLE.NEW_CONTENT;
                return (
                  <li key={ch.id} data-testid={`snapshots-change-${ch.id}`}
                       className="rounded-lg border border-border-hairline bg-bg-panel p-4">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${style.cls}`}>
                        {style.label}
                      </span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${CONF_STYLE[ch.confidence] || CONF_STYLE.LOW}`}>
                        {ch.confidence}
                      </span>
                      <span className="text-[10px] text-text-subtle">
                        {new Date(ch.detected_at).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" })}
                      </span>
                    </div>
                    <p className="text-sm text-text-primary leading-relaxed">{ch.summary_nl}</p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Snapshot log */}
        <div className="lg:col-span-2">
          <h2 className="text-xs text-text-muted font-semibold mb-3">
            Snapshots ({snaps.length})
          </h2>
          {snaps.length === 0 ? (
            <div data-testid="snapshots-empty" className="rounded-xl border border-dashed border-border-hairline bg-bg-panel p-8 text-center">
              <p className="text-sm text-text-muted">Nog geen snapshots. Klik op "Snapshot nu" om te starten.</p>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="snapshots-list">
              {snaps.map((s) => {
                const isOpen = openSnap === s.id;
                const ok = s.http_status === 200;
                return (
                  <li key={s.id} data-testid={`snapshot-item-${s.id}`}
                       className="rounded-lg border border-border-hairline bg-bg-panel">
                    <button
                      type="button"
                      onClick={() => setOpenSnap(isOpen ? null : s.id)}
                      className="w-full p-3 flex items-center gap-2 text-left"
                      data-testid={`snapshot-toggle-${s.id}`}
                    >
                      {isOpen ? <ChevronDown className="w-3 h-3 text-text-subtle" /> : <ChevronRight className="w-3 h-3 text-text-subtle" />}
                      {ok ? <CircleCheck className="w-3.5 h-3.5 text-pos-green" /> : <AlertTriangle className="w-3.5 h-3.5 text-warn-amber" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-text-primary font-medium truncate">
                          {s.homepage_title || `HTTP ${s.http_status}`}
                        </div>
                        <div className="text-[10px] text-text-muted">
                          {new Date(s.snapshot_at).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" })}
                        </div>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="border-t border-border-hairline p-3 text-xs text-text-primary/85 space-y-2" data-testid={`snapshot-detail-${s.id}`}>
                        <div><span className="text-text-muted text-[9px]">Status</span><br />HTTP {s.http_status}{s.fetch_error && ` — ${s.fetch_error}`}</div>
                        {s.meta_description && <div><span className="text-text-muted text-[9px]">Meta</span><br />{s.meta_description}</div>}
                        {s.headings_h1_h2?.h2?.length > 0 && (
                          <div><span className="text-text-muted text-[9px]">Secties (h2)</span><br />
                            {s.headings_h1_h2.h2.join(" · ")}
                          </div>
                        )}
                        {s.calls_to_action?.length > 0 && (
                          <div><span className="text-text-muted text-[9px]">CTA's</span><br />
                            {s.calls_to_action.join(" · ")}
                          </div>
                        )}
                        <a href={s.domain} target="_blank" rel="noreferrer" className="text-accent-teal hover:underline inline-flex items-center gap-1">
                          Bekijk originele pagina <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
      );
      })()}
    </AppLayout>
  );
}
